import { type ErrorInfo, useCallback, useMemo } from "react";
import type {
	ClientToHostMessage,
	MessagePayload,
} from "@rubyplay/effw-connector";

import { CommunicatorProvider, useCommunicator } from "@/contexts/CommunicatorContext";
import { SnapshotBridgeProvider } from "@/contexts/SnapshotBridgeContext";
import { CommandRegistryProvider } from "@/commands/CommandRegistryContext";

import { Cheats } from "@/components/Cheats";
import { WidgetDock } from "@/components/WidgetDock";
import { PopupSurface } from "@/components/PopupSurface";

import { CaptainUpCheatCommands } from "@/cheats/CaptainUpCheatCommands";
import { EFProtocolCheatCommands } from "@/cheats/EFProtocolCheatCommands";
import type { TooltipPosition } from "@/integrations/captainup/CaptainUpTypes";

import EfFatalErrorBoundary from "@/components/EfFatalErrorBoundary";

import {
	buildFatalPayload,
	hasFatalBeenReported,
	markFatalReported,
} from "@/runtime/efRuntimeProcess";

import useEfAppRuntime from "@/runtime/useEfAppRuntime";
import { useHostLayout } from "@/hooks/useHostLayout";
import { FeatureRuntimeProvider } from "@/features/runtime/FeatureRuntimeContext";
import { FeatureStateProvider } from "@/features/runtime/FeatureStateContext";
import type { EFFeatureDockRendererContext } from "@/features/runtime/FeatureTypes";

function InnerApp({
	runtime,
}: {
	runtime: ReturnType<typeof useEfAppRuntime>;
}) {
	const { freeAreas, platform, orientation } = useHostLayout();

	const {
		host,
		providerRuntime,
		shell,
		featureRuntime,
		featureRegistry,
		featureSnapshots,
	} = runtime;

	const activeFeatureId = featureRuntime.view.selection.activeFeatureId;

	const activeFeatureDefinition = useMemo(() => {
		if (!activeFeatureId) {
			return null;
		}

		return featureRegistry.registry.get(activeFeatureId) ?? null;
	}, [activeFeatureId, featureRegistry.registry]);

	const activeDockFeatureId = useMemo(() => {
		for (const snapshot of featureSnapshots) {
			if (!snapshot.activation.active) {
				continue;
			}

			const definition = featureRegistry.registry.get(snapshot.featureId);
			if (!definition?.capabilities.usesDock) {
				continue;
			}

			return snapshot.featureId;
		}

		return null;
	}, [featureRegistry.registry, featureSnapshots]);

	const activeDockFeatureDefinition = useMemo(() => {
		if (!activeDockFeatureId) {
			return null;
		}

		return featureRegistry.registry.get(activeDockFeatureId) ?? null;
	}, [activeDockFeatureId, featureRegistry.registry]);

	const activeSnapshotWidgetId =
		activeFeatureDefinition?.snapshotWidgetId ?? null;

	const dockRendererContext = useMemo<EFFeatureDockRendererContext>(
		() => ({
			registerDockHost: (target) => shell.setDockHost(target),
		}),
		[shell.setDockHost]
	);

	const dockContent = useMemo(() => {
		if (
			!activeDockFeatureId ||
			!activeDockFeatureDefinition?.renderer?.renderDock
		) {
			return null;
		}

		return activeDockFeatureDefinition.renderer.renderDock({
			featureId: activeDockFeatureId,
			dock: dockRendererContext,
		});
	}, [
		activeDockFeatureDefinition,
		activeDockFeatureId,
		dockRendererContext,
	]);

	const handleDockActivate = () => {
		if (!activeFeatureId) {
			return;
		}

		featureRuntime.actions.openFeaturePopup(activeFeatureId);
	};

	const handleSetTooltipPosition = useCallback(
		(position: TooltipPosition) => {
			providerRuntime.provider?.setTooltipPosition(position);
		},
		[providerRuntime.provider]
	);

	if (!host.hostConfigured) {
		return null;
	}

	return (
		<>
			<CaptainUpCheatCommands
				activeFeatureId={activeFeatureId}
				featureRegistry={featureRegistry.registry}
				activateFeature={featureRuntime.actions.activateFeature}
				onSetTooltipPosition={handleSetTooltipPosition}
				openFeaturePopup={featureRuntime.actions.openFeaturePopup}
				closeFeaturePopup={featureRuntime.actions.closeFeaturePopup}
				onRetryBoot={providerRuntime.requestFreshProviderBoot}
			/>

			<EFProtocolCheatCommands />

			<WidgetDock
				freeAreas={freeAreas}
				platform={platform}
				orientation={orientation}
				onDockStateChange={shell.setDockState}
				hitAreaName="captainup.openPopup"
				onActivate={handleDockActivate}
				provider={providerRuntime.provider}
				activeFeatureId={activeFeatureId}
				activeSnapshotWidgetId={activeSnapshotWidgetId}
				activeDockHasContent={shell.activeDockHasContent}
			>
				{dockContent}
			</WidgetDock>

			<PopupSurface
				visible={shell.popupVisible}
				fullscreenOpenIntentId={shell.popupOpenIntentId}
				onClose={shell.handlePopupClose}
				onSurfaceStateChange={shell.setPopupState}
				fullscreenBackgroundColor="rgba(0, 0, 0, 0)"
				hasCloseBtn={false}
			/>
		</>
	);
}

function RuntimeBoundApp() {
	const runtime = useEfAppRuntime();

	return (
		<FeatureRuntimeProvider value={runtime.featureRuntime}>
			<FeatureStateProvider
				value={{
					definitions: runtime.featureRegistry.definitions,
					registry: runtime.featureRegistry.registry,
					featureSnapshots: runtime.featureSnapshots,
				}}
			>
				<InnerApp runtime={runtime} />
			</FeatureStateProvider>
		</FeatureRuntimeProvider>
	);
}

function InnerAppWithBoundary() {
	const { sendFatalError } = useCommunicator();

	const handleBoundaryFatal = useCallback(
		(error: Error, errorInfo: ErrorInfo) => {
			if (hasFatalBeenReported()) {
				return;
			}

			markFatalReported();

			const payload: MessagePayload<ClientToHostMessage, "FatalError"> = {
				...buildFatalPayload("RUNTIME", error),
				stack: errorInfo.componentStack
					? `${error.stack ?? ""}\n${errorInfo.componentStack}`.trim()
					: error.stack,
			};

			sendFatalError(payload);
		},
		[sendFatalError]
	);

	return (
		<EfFatalErrorBoundary onFatal={handleBoundaryFatal}>
			<RuntimeBoundApp />
		</EfFatalErrorBoundary>
	);
}

export default function App() {
	return (
		<CommandRegistryProvider>
			<CommunicatorProvider>
				<SnapshotBridgeProvider>
					<Cheats />
					<InnerAppWithBoundary />
				</SnapshotBridgeProvider>
			</CommunicatorProvider>
		</CommandRegistryProvider>
	);
}